import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PageBreadcrumbComponent } from '../../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { AuthService } from '../../../auth-pages/services/auth.service';
import { MeetingEmailTemplateApiService } from '../../services/meeting-email-template-api.service';
import { MeetingEmailTemplateDrawerService } from '../../services/meeting-email-template-drawer.service';
import {
  EMAIL_TEMPLATE_TYPE_LABELS,
  EMAIL_TEMPLATE_TYPE_USAGE,
  EmailTemplateType,
  MeetingEmailTemplateResponse,
  MeetingSystemEmailTemplateResponse,
  PageResponse,
} from '../../models/meeting.model';
import { ConfirmDialogService } from '../../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../../shared/services/toast.service';
import { HasPermissionDirective } from '../../../../shared/directives/has-permission.directive';

@Component({
  selector: 'app-config-email-templates-list',
  standalone: true,
  imports: [CommonModule, FormsModule, PageBreadcrumbComponent, HasPermissionDirective],
  templateUrl: './email-templates-list.component.html',
})
export class EmailTemplatesListComponent implements OnInit, OnDestroy {
  items: MeetingEmailTemplateResponse[] = [];
  /**
   * INC 81 — plantillas predeterminadas del sistema, en solo lectura.
   *
   * Se listan arriba de las del negocio porque son las que SE ENVÍAN mientras el cliente no
   * marque una propia como predeterminada. Antes el listado solo mostraba las suyas y, sin
   * ninguna default, el sistema mandaba una cualquiera (la más reciente) sin que nadie lo
   * supiera.
   */
  systemDefaults: MeetingSystemEmailTemplateResponse[] = [];
  loading = false;
  error = '';

  filterType: EmailTemplateType | '' = '';

  readonly types: EmailTemplateType[] = [
    'confirmation_invitee', 'confirmation_host',
    'reminder_invitee', 'reminder_host',
    'followup', 'no_show', 'reschedule', 'cancellation',
  ];

  private readonly destroy$ = new Subject<void>();

  constructor(
    private api: MeetingEmailTemplateApiService,
    private drawerService: MeetingEmailTemplateDrawerService,
    private authService: AuthService,
    private router: Router,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService
  ) {}

  ngOnInit(): void {
    this.load();
    this.drawerService.saved$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.load());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private get businessId(): number {
    return this.authService.getCurrentUser()?.businessId || 1;
  }

  /** INC 64: etiqueta en español del tipo de plantilla. */
  typeLabel(t: EmailTemplateType | string): string {
    return EMAIL_TEMPLATE_TYPE_LABELS[t as EmailTemplateType] ?? t;
  }

  /** INC 80: ¿el sistema envía este tipo de plantilla en algún momento? */
  seEnvia(t: EmailTemplateType | string): boolean {
    return EMAIL_TEMPLATE_TYPE_USAGE[t as EmailTemplateType]?.automatico ?? true;
  }

  /** INC 80: explicación del disparador (tooltip de la etiqueta). */
  usoDe(t: EmailTemplateType | string): string {
    return EMAIL_TEMPLATE_TYPE_USAGE[t as EmailTemplateType]?.cuando ?? '';
  }

  load(): void {
    this.loading = true;
    this.error = '';

    const obs: Observable<MeetingEmailTemplateResponse[] | PageResponse<MeetingEmailTemplateResponse>> =
      this.filterType
        ? this.api.listByType(this.businessId, this.filterType)
        : this.api.list(this.businessId, 0, 200);

    obs.subscribe({
      next: (data) => {
        this.items = Array.isArray(data) ? data : data.content;
        this.loading = false;
      },
      error: (err: any) => {
        this.error = err.error?.message || 'Error cargando plantillas';
        this.loading = false;
      },
    });

    // INC 81: fallo suave — si no se pueden leer, el listado sigue mostrando las del negocio.
    this.api.listSystemDefaults().subscribe({
      next: (data) => (this.systemDefaults = data),
      error: () => (this.systemDefaults = []),
    });
  }

  /**
   * INC 81 — plantillas del sistema visibles con el filtro activo. Solo se muestran las de
   * los tipos que el usuario está mirando, para no ensuciar una lista ya filtrada.
   */
  get visibleSystemDefaults(): MeetingSystemEmailTemplateResponse[] {
    if (!this.filterType) return this.systemDefaults;
    return this.systemDefaults.filter((s) => s.templateType === this.filterType);
  }

  /**
   * INC 81 — ¿este tipo se está enviando ahora mismo con la plantilla del sistema?
   * Es decir: el negocio no tiene ninguna plantilla ACTIVA y marcada por defecto para él.
   */
  usandoPlantillaDelSistema(templateType: string): boolean {
    return !this.items.some(
      (t) => t.templateType === templateType && t.isDefault && t.isActive !== false
    );
  }

  crear(): void {
    this.drawerService.open();
  }

  editar(item: MeetingEmailTemplateResponse): void {
    this.drawerService.open({ templateId: item.id });
  }

  editarPagina(item: MeetingEmailTemplateResponse): void {
    this.router.navigate(['/meeting/config/email-templates/editar', item.id]);
  }

  async eliminar(item: MeetingEmailTemplateResponse): Promise<void> {
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar plantilla',
      message: `¿Eliminar plantilla "${item.name}"?`
    });
    if (!ok) return;
    this.api.delete(item.id, this.businessId).subscribe({
      next: () => { this.load(); this.toast.deleted('Plantilla de email', `Se eliminó "${item.name}" correctamente.`); },
      error: (err) => { this.toast.error({ title: 'Error', message: err.error?.message || 'Error al eliminar' }); },
    });
  }
}
